import React, { useState } from "react";
import { Invitation } from "../lib/supabase";
import EventBadges from "./EventBadges";
import ConfirmationDialog from "./ConfirmationDialog";
import useConfirmationDialog from "../hooks/useConfirmationDialog";
import AddInvitationForm from "./AddInvitationForm";
import { CreateInvitationData } from "../api/invitations";

interface InvitationsTableProps {
  invitations: Invitation[];
  onDelete: (invitationId: string) => void;
  onEdit: (invitation: Invitation) => void;
  onUpdatePrintedCode: (invitationId: string, printedCode: boolean) => void;
  onUpdateFinalized: (invitationId: string, finalized: boolean) => void;
  onUpdateSent: (invitationId: string, sent: boolean) => void;
  editingInvitationId?: string;
  onUpdateInvitation: (
    invitationId: string,
    data: CreateInvitationData
  ) => Promise<void>;
  onCancelEdit: () => void;
}

const InvitationsTable: React.FC<InvitationsTableProps> = ({
  invitations,
  onDelete,
  onEdit,
  onUpdatePrintedCode,
  onUpdateFinalized,
  onUpdateSent,
  editingInvitationId,
  onUpdateInvitation,
  onCancelEdit,
}) => {
  const { dialogState, showConfirmation, handleConfirm, handleCancel } =
    useConfirmationDialog();

  const [isAdminSectionCollapsed, setIsAdminSectionCollapsed] = useState(false);

  const handleDelete = (invitationId: string) => {
    showConfirmation(
      {
        title: "Delete Invitation",
        message:
          "Are you sure you want to delete this invitation? This action cannot be undone.",
        confirmText: "Delete",
        cancelText: "Cancel",
        type: "danger",
      },
      () => onDelete(invitationId)
    );
  };

  const formatRSVPStatus = (invitation: Invitation) => {
    const responses = [];
    if (invitation.rsvp_ceremony) responses.push("Ceremony");
    if (invitation.rsvp_luncheon) responses.push("Luncheon");
    if (invitation.rsvp_reception) responses.push("Reception");
    return responses.length > 0 ? responses.join(", ") : "No response";
  };

  return (
    <>
      <div className="items-table">
        <table>
          <thead>
            <tr>
              <th>
                <button
                  className="collapse-btn"
                  onClick={() =>
                    setIsAdminSectionCollapsed(!isAdminSectionCollapsed)
                  }
                  title={
                    isAdminSectionCollapsed
                      ? "Expand admin controls"
                      : "Collapse admin controls"
                  }
                >
                  {isAdminSectionCollapsed ? "+" : "−"}
                </button>
              </th>
              {!isAdminSectionCollapsed && (
                <>
                  <th>Printed</th>
                  <th>Finalized</th>
                  <th>Sent</th>
                </>
              )}
              <th>Code</th>
              <th>Guest Names</th>
              <th>Events Invited</th>
              <th>RSVP Status</th>
              <th>Family Attending</th>
              <th>Plus One</th>
              <th>Plus One Name</th>
              <th>Dietary Restrictions</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {invitations.map((invitation) => (
              <React.Fragment key={invitation.id}>
                {editingInvitationId === invitation.id ? (
                  <tr className="editing-row">
                    <td colSpan={isAdminSectionCollapsed ? 10 : 13}>
                      <AddInvitationForm
                        onSubmit={(data) =>
                          onUpdateInvitation(invitation.id, data)
                        }
                        onCancel={onCancelEdit}
                        invitation={invitation}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td>
                      {!isAdminSectionCollapsed && (
                        <div className="admin-controls-expanded">
                          <div className="checkbox-group">
                            <label>
                              <input
                                type="checkbox"
                                checked={invitation.printed_code || false}
                                onChange={(e) =>
                                  onUpdatePrintedCode(
                                    invitation.id,
                                    e.target.checked
                                  )
                                }
                                className="printed-code-checkbox"
                              />
                              <span>P</span>
                            </label>
                          </div>
                        </div>
                      )}
                      {isAdminSectionCollapsed && (
                        <div className="admin-controls-collapsed">
                          <div className="status-indicators">
                            <span
                              className={`status-dot ${
                                invitation.printed_code ? "active" : ""
                              }`}
                              title="Printed Code"
                              onClick={(e) => {
                                e.stopPropagation();
                                onUpdatePrintedCode(
                                  invitation.id,
                                  !invitation.printed_code
                                );
                              }}
                            ></span>
                            <span
                              className={`status-dot ${
                                invitation.finalized ? "active" : ""
                              }`}
                              title="Finalized"
                              onClick={(e) => {
                                e.stopPropagation();
                                onUpdateFinalized(
                                  invitation.id,
                                  !invitation.finalized
                                );
                              }}
                            ></span>
                            <span
                              className={`status-dot ${
                                invitation.sent ? "active" : ""
                              }`}
                              title="Sent"
                              onClick={(e) => {
                                e.stopPropagation();
                                onUpdateSent(invitation.id, !invitation.sent);
                              }}
                            ></span>
                          </div>
                        </div>
                      )}
                    </td>
                    {!isAdminSectionCollapsed && (
                      <>
                        <td>
                          <input
                            type="checkbox"
                            checked={invitation.finalized || false}
                            onChange={(e) =>
                              onUpdateFinalized(invitation.id, e.target.checked)
                            }
                            className="finalized-checkbox"
                          />
                        </td>
                        <td>
                          <input
                            type="checkbox"
                            checked={invitation.sent || false}
                            onChange={(e) =>
                              onUpdateSent(invitation.id, e.target.checked)
                            }
                            className="sent-checkbox"
                          />
                        </td>
                      </>
                    )}
                    <td>
                      <strong className="arial-font">{invitation.code}</strong>
                    </td>
                    <td>{invitation.guest_names}</td>
                    <td>
                      <EventBadges
                        ceremony={invitation.ceremony}
                        luncheon={invitation.luncheon}
                        reception={invitation.reception}
                        plusOne={invitation.plus_one}
                        plusOneName={invitation.plus_one_name}
                      />
                    </td>
                    <td>
                      <div className="rsvp-status">
                        {formatRSVPStatus(invitation)}
                      </div>
                    </td>
                    <td>
                      {invitation.family_allowed ? (
                        <span className="family-info">
                          {invitation.family_attending !== undefined
                            ? `${invitation.family_attending} attending`
                            : "No response"}
                        </span>
                      ) : (
                        <span className="status unavailable">Not allowed</span>
                      )}
                    </td>
                    <td>
                      {invitation.plus_one ? (
                        invitation.bringing_plus_one !== undefined ? (
                          invitation.bringing_plus_one ? (
                            <span className="event-badge plus-one">Yes</span>
                          ) : (
                            <span className="status unavailable">No</span>
                          )
                        ) : (
                          <span className="status pending">No response</span>
                        )
                      ) : (
                        <span className="status unavailable">Not allowed</span>
                      )}
                    </td>
                    <td>
                      {invitation.plus_one_name || (
                        <span className="status empty">—</span>
                      )}
                    </td>
                    <td>
                      <div className="dietary-restrictions">
                        {invitation.dietary_restrictions ? (
                          <span
                            className="dietary-text"
                            title={invitation.dietary_restrictions}
                          >
                            {invitation.dietary_restrictions.length > 30
                              ? `${invitation.dietary_restrictions.substring(
                                  0,
                                  30
                                )}...`
                              : invitation.dietary_restrictions}
                          </span>
                        ) : (
                          <span className="status empty">None</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => onEdit(invitation)}
                          className="edit-btn"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(invitation.id)}
                          className="delete-btn"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmationDialog
        isOpen={dialogState.isOpen}
        title={dialogState.title}
        message={dialogState.message}
        confirmText={dialogState.confirmText}
        cancelText={dialogState.cancelText}
        type={dialogState.type}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
};

export default InvitationsTable;
